// The shell's nav vocabulary and its single resolver. Sidebar,
// CommandPalette and RouteAnnouncer all read the same resolved list, so
// the three can never disagree about which screens exist for the org
// and role at hand — the palette used to offer "Contratistas" to a
// facility gerente whose sidebar (correctly) never showed it, and the
// announcer used to fall back to a raw pathname for a route the sidebar
// named. `resolveNavItems` is pure (recipe + session + counts in, items
// out) so the gating can be tested without React; `useVisibleNavItems`
// is the hook the three components call.
//
// Every gate here is a recipe capability or a role-set predicate —
// never recipe.slug (§0.4(6), ADR-0072).

import { useMemo } from "react";
import {
  isIaChatRole,
  isManagementAnalyticsRole,
  isReadOnlyRole,
  isTeamRosterReadRole,
  type OrgMember,
  type RecipeMeResponse,
} from "@bitacorapro/api-types";
import { ADMIN_SECTION_KEYS, type AdminSection } from "@bitacorapro/core-engine";
import { adminWords } from "./admin/words";
import { useFacilityDashboard, useMyOrganization, useRecipe, useRoster } from "./api/queries";
import type { Session } from "./api/session";
import { useSession } from "./auth/useSession";
import {
  canAdministerOrg,
  canImportUnits,
  canManageChecklistTemplates,
  isContextLayerEnabled,
  canReadProviderRoster,
  canUseChecklistsSection,
  hasAdminModule,
  isFacilityDashboardEnabled,
  lacksDashboardSurface,
  type OrgAdminOpts,
} from "./capabilities";
import { threadWords } from "./postventa/words";
import type { Icon } from "../components/icons";

// The sidebar's headings. `cartera` only exists at resolution time: a
// group-rooted lifecycle vertical (postventa v2) frames its global
// entries as the cartera level the contextual project block sits above,
// so Contratistas / Enlaces / AI Insights move there from their static
// group. Facility never sees it — its two groups stay Operación /
// Gestión, untouched.
export type NavGroup = "principal" | "cartera" | "gestion";

export type NavItem = {
  key: string;
  label: string;
  path: string;
  icon: keyof typeof Icon;
  group: NavGroup;
  alert?: boolean;
  // Only shown when the org's recipe has a non-empty
  // observationStatuses vocabulary (ADR-0058) — never gated on
  // recipe.slug (§0.4(6)).
  requiresObservationLifecycle?: boolean;
  // Only shown to org-scoped write roles (ADR-0079 Decision 7) — the
  // Configuración screen's checklist-template panel 403s server-side
  // for store_manager/brand_manager on the read too, so surfacing the
  // nav entry for them would just be a dead link into an empty page.
  requiresChecklistTemplateAdmin?: boolean;
  // Only shown to IA_CHAT_ROLE_KEYS (ADR-0139) — the chat is role-scoped
  // server-side, so a facility_supervisor sees its own stores' facts;
  // roles outside the set 403 at POST /ia/chat. The entry also carries
  // `requiresFacilityDashboard`: POST /ia/chat answers VERTICAL_REQUIRED
  // to any non-facility recipe, and the screen's copy and prompt speak
  // of tiendas, so a postventa gerente/jefe would see a dead entry.
  requiresIaChat?: boolean;
  // Gated on TEAM_ROSTER_READ_ROLE_KEYS (product decision 2026-09-03,
  // amends ADR-0089). ADR-0089 originally shipped this nav item hidden
  // from facility_supervisor as a conservative safe default "pending a
  // product decision" — that decision arrived and reversed it:
  // supervisors are the ones who onboard jefes de tienda, and they are
  // already inviter-eligible, so the roster is their working surface,
  // not a courtesy read. The same pass narrowed the other end —
  // store_manager is now excluded too: a jefe manages one store's
  // operation, not the org's people, and isn't inviter-eligible either,
  // so there is no action behind the read for them, only every
  // colleague's email and activity heartbeat.
  requiresTeamRosterRead?: boolean;
  // Sólo para un rol de alcance organizacional. Un rol acotado a un
  // proyecto (jefe de proyecto, `scopeType: "asset_group"`) administra la
  // postventa de su proyecto, no las cuentas ni la receta de la
  // inmobiliaria — el handoff del panel los sacó del sidebar por eso
  // (iteración 2026-09-16). Se gatea por el primitivo de alcance de la
  // receta y no por la clave del rol, así que una vertical que mañana
  // declare otro rol acotado hereda la regla sin tocar este archivo.
  requiresOrgScope?: boolean;
  // Only shown when the recipe enables the facility dashboard — the
  // org-wide open-findings list is that dashboard's working surface. A
  // lifecycle vertical (postventa) has no org-wide observations screen
  // any more: the panel postventa v2 (decisión 4 del 2026-09-29) retired
  // the «Por revisar» queue, and /observaciones now forwards to the
  // active project's observations (ObservacionesRoute). The contextual
  // project sidebar that replaces it is a separate slice.
  requiresFacilityDashboard?: boolean;
  // ADR-0144 §2 — the unit importer. Recipe declares `unitProfile` and
  // the caller may import (canImportUnits): the gerente de postventa,
  // the org admin, or a platform admin.
  requiresUnitImport?: boolean;
  // ADR-0173 fase 2 — only while the org's `context_ingest` flag is on, and
  // only for roles that reach the screen: org-scoped (upload, review,
  // activate) or brand-scoped (read its brand). Store-scoped roles never
  // see it; the server 403s them anyway.
  requiresContextLayer?: boolean;
  // Hidden from read-only roles (READ_ONLY_ROLE_KEYS — brand_manager in
  // v1, founder 2026-10-19, ADR-0164): core-api answers 403
  // ROLE_READ_ONLY to every write they send. Only for screens whose job
  // is a write, or whose read is denied to that role anyway — a screen
  // with useful read content stays and hides its controls instead.
  hiddenForReadOnly?: boolean;
  // Autogestión (plan 2026-10-30 §1.5): sólo existe si la receta declara
  // `adminModule`. Para cualquier otra receta la entrada no está.
  requiresAdminModule?: boolean;
  // `false` = registrada pero sin pantalla (o sin backend) todavía: no se
  // muestra en ningún lado (regla «ocultar acciones sin capacidad»). Entra
  // sola cuando se construye, cambiando esta bandera. Ausente = disponible.
  available?: boolean;
  // Hija de un acordeón del sidebar (hoy sólo Administración). El
  // Sidebar la dibuja debajo de su padre; ⌘K y el anunciador de rutas la
  // ven como una entrada más de la lista plana.
  parentKey?: string;
  // Sólo para quien administra la organización (canAdministerOrg: admin,
  // administrador de plataforma o el rol `orgAdmin` de la receta). Es el
  // gate por defecto de una sección de Administración nueva, que no
  // hereda el de una entrada suelta.
  requiresOrgAdmin?: boolean;
  // Gate propio de una sección cuya pantalla es más estricta que la entrada
  // suelta que absorbe (Contratistas: el carril de lectura de la nómina,
  // espejo de GET /providers). Se evalúa además de los anteriores, con la
  // misma firma que RequireRoleCapability en App.tsx.
  allow?: (
    recipe: RecipeMeResponse | undefined,
    role: string | undefined,
    opts: OrgAdminOpts
  ) => boolean;
};

// A nav entry after resolution: its badge is attached, and the label /
// path are already the ones this org sees (not the pre-recipe fallback).
export type VisibleNavItem = NavItem & {
  // null = no badge (not a misleading 0).
  count: number | null;
};

export const ADMIN_NAV_KEY = "administracion";
export const ADMIN_BASE_PATH = "/administracion";

// Las secciones de Administración, en el orden del menú (el del enum de
// la receta). La receta decide cuáles declara; este registro decide cuáles
// tienen pantalla hoy. Una sección que reemplaza una entrada suelta
// (`replaces`) hereda sus gates de rol tal cual —la pantalla es la misma,
// sólo cambia de lugar— y esa entrada suelta desaparece del nivel
// superior; si además su pantalla nueva exige algo más, lo dice en
// `allow`. Las demás quedan `available: false` hasta que se construyan:
//   - resumen: su ruta es la del índice (/administracion), la portada del
//     módulo. Para un rol que no administra, el índice redirige a la
//     primera sección visible (App.tsx, AdminIndexRedirect).
//   - checklists: además del gate de administrador exige que la receta
//     configure checklists por plantilla (canUseChecklistsSection); el
//     panel viejo de Configuración sigue ahí, alcanzable por URL.
export type AdminSectionNav = {
  section: AdminSection;
  path: string;
  available: boolean;
  replaces?: string;
  allow?: NavItem["allow"];
};

export const ADMIN_SECTION_NAV: readonly AdminSectionNav[] = [
  { section: "resumen", path: ADMIN_BASE_PATH, available: true },
  { section: "empresa", path: `${ADMIN_BASE_PATH}/empresa`, available: true },
  { section: "equipo", path: `${ADMIN_BASE_PATH}/equipo`, available: true, replaces: "equipo" },
  // activos y mantenciones no llevan `replaces`: /tiendas (operación) sigue
  // existiendo y el catálogo de mantenciones no tenía pantalla.
  { section: "activos", path: `${ADMIN_BASE_PATH}/activos`, available: true },
  {
    section: "checklists",
    path: `${ADMIN_BASE_PATH}/checklists`,
    available: true,
    allow: canUseChecklistsSection,
  },
  { section: "mantenciones", path: `${ADMIN_BASE_PATH}/mantenciones`, available: true },
  // Absorbe la entrada suelta (sus gates: ciclo de observaciones, no
  // read-only) y además exige el carril de lectura de la nómina: la
  // pantalla nueva (routes/administracion/Contratistas) es del gerente y
  // del supervisor, que asignan; un jefe de tienda no tiene nada que ver
  // ahí (GET /providers le responde 403).
  {
    section: "contratistas",
    path: `${ADMIN_BASE_PATH}/contratistas`,
    available: true,
    replaces: "contratistas",
    allow: canReadProviderRoster,
  },
  { section: "enlaces", path: `${ADMIN_BASE_PATH}/enlaces`, available: true, replaces: "enlaces" },
  { section: "plazos", path: `${ADMIN_BASE_PATH}/plazos`, available: false },
  { section: "registro", path: `${ADMIN_BASE_PATH}/registro`, available: true },
];

// The route of a section (the registry's, so App.tsx, the crumbs and the
// nav never spell it twice).
export function adminSectionPath(section: AdminSection): string {
  return ADMIN_SECTION_NAV.find((s) => s.section === section)?.path ?? ADMIN_BASE_PATH;
}

// The sections a recipe declares that have a screen today, in menu order.
// Empty for a recipe without `adminModule`.
export function declaredAdminSections(
  recipe: RecipeMeResponse | undefined
): readonly AdminSectionNav[] {
  if (!hasAdminModule(recipe)) return [];
  const declared = new Set(recipe?.adminModule?.sections ?? []);
  return ADMIN_SECTION_KEYS.filter((k) => declared.has(k))
    .map((k) => ADMIN_SECTION_NAV.find((s) => s.section === k))
    .filter((s): s is AdminSectionNav => s !== undefined && s.available);
}

export const NAV_ITEMS: NavItem[] = [
  {
    key: "overview",
    label: "Dashboard",
    path: "/",
    icon: "Home",
    group: "principal",
  },
  // "Tiendas" is the facility default; group-rooted non-facility
  // verticals (postventa) get "Proyectos"/`/proyectos` at render time
  // — see the override in resolveNavItems() below. Keep this literal as
  // the pre-recipe-load fallback only: a loaded recipe with no dashboard
  // surface gets no nav at all (resolveNavItems returns []).
  { key: "tiendas", label: "Tiendas", path: "/tiendas", icon: "Store", group: "principal" },
  // Facility's always-visible open-findings entry, badge = open findings
  // across the caller's stores. Keyed on the dashboards capability,
  // never on the slug; absent for every other vertical. `alert` keeps
  // the red badge facility has always had (open findings are the
  // working queue, not a neutral count).
  {
    key: "observaciones",
    label: "Observaciones",
    path: "/observaciones",
    icon: "Inbox",
    group: "principal",
    alert: true,
    requiresFacilityDashboard: true,
  },
  // Autogestión (plan 2026-10-30 §1.5): el calendario entra en Operación
  // con el contador rojo de eventos atrasados. Ni la pantalla ni su
  // backend existen todavía en esta rama, así que queda registrado y
  // oculto (`available: false`) hasta que existan.
  {
    key: "calendario",
    label: "Calendario",
    path: "/calendario",
    icon: "Calendar",
    group: "principal",
    alert: true,
    requiresAdminModule: true,
    available: false,
  },
  {
    key: "ia",
    label: "AI Insights",
    path: "/ia",
    icon: "Sparkles",
    group: "principal",
    requiresIaChat: true,
    requiresFacilityDashboard: true,
  },
  // No `requiresOrgScope` here: TEAM_ROSTER_READ_ROLE_KEYS already
  // admits facility_supervisor (asset_group scope) and brand_manager
  // (brand scope) on purpose — they onboard their own people — and the
  // route is gated by RequireTeamRosterRead in App.tsx. An org-scope
  // gate on top of the role set silently hid the roster from exactly the
  // roles the product decision let in.
  {
    key: "equipo",
    label: "Equipo",
    path: "/equipo",
    icon: "Users",
    group: "gestion",
    requiresTeamRosterRead: true,
  },
  {
    // La nómina vive en Gestión y sólo para verticales con ciclo de
    // observaciones: es la que alimenta el drawer de asignación, y sin
    // ese ciclo no hay a quién asignarle nada.
    key: "contratistas",
    label: "Contratistas",
    path: "/contratistas",
    icon: "Users",
    group: "gestion",
    requiresObservationLifecycle: true,
    // The roster only feeds the assignment drawer, and GET /providers is
    // limited to admin and the assigning_party lane — a read-only role
    // declares no lane, so the screen would only show an error.
    hiddenForReadOnly: true,
  },
  {
    // Managing public links (revoke, access history) is the screen's
    // job; a read-only role has neither, only an org-wide list of
    // recipients outside its own scope.
    key: "enlaces",
    label: "Enlaces compartidos",
    path: "/enlaces",
    icon: "External",
    group: "gestion",
    requiresObservationLifecycle: true,
    hiddenForReadOnly: true,
  },
  {
    key: "carga-unidades",
    label: "Cargar unidades",
    path: "/carga-unidades",
    icon: "Units",
    group: "gestion",
    requiresUnitImport: true,
  },
  {
    // ADR-0144 §4 — documentos del proyecto (manual, reglamento,
    // planos). Mismo gating que el importador: misma persona, misma
    // receta (canImportUnits).
    key: "documentos",
    label: "Documentos",
    path: "/documentos",
    icon: "Folder",
    group: "gestion",
    requiresUnitImport: true,
  },
  {
    key: "contexto",
    label: "Contexto del cliente",
    path: "/contexto",
    icon: "Database",
    group: "gestion",
    requiresContextLayer: true,
  },
  {
    key: "configuracion",
    label: "Configuración",
    path: "/configuracion",
    icon: "Settings",
    group: "gestion",
    requiresChecklistTemplateAdmin: true,
    requiresOrgScope: true,
    hiddenForReadOnly: true,
  },
  // Autogestión: el padre del acordeón. Sin gate propio — se muestra si
  // al menos una de sus secciones pasa (resolveNavItems), y sus hijas
  // salen de ADMIN_SECTION_NAV.
  {
    key: ADMIN_NAV_KEY,
    label: "Administración",
    path: ADMIN_BASE_PATH,
    icon: "Settings",
    group: "gestion",
    requiresAdminModule: true,
  },
  // Gestión del handoff: Administración y Contexto de la IA. La pantalla
  // de contexto no existe en esta rama — registrada y oculta.
  {
    key: "contexto-ia",
    label: "Contexto de la IA",
    path: "/contexto-ia",
    icon: "Sparkles",
    group: "gestion",
    requiresAdminModule: true,
    available: false,
  },
];

export type ResolveNavItemsInput = {
  recipe: RecipeMeResponse | undefined;
  session: Session | null;
  // Facility aggregate badges. null while not loaded or not applicable.
  facilityCounts: { assets: number | null; openFindings: number | null };
  contextLayerEnabled?: boolean;
  // Badges of the Administración children (autogestión §2.3). null while
  // not loaded or not applicable — never a misleading 0.
  adminCounts: { equipoActivos: number | null };
};

// The badge of «Equipo y accesos»: the active members the screen lists
// — the roster filtered to the roles the recipe declares (the client
// mirror of `view=roster`, same as routes/administracion/Equipo.tsx),
// minus the deactivated ones. Pure, so the Sidebar test and the screen
// can never disagree on what the number means.
export function activeTeamMemberCount(
  members: readonly OrgMember[],
  recipe: RecipeMeResponse | undefined
): number {
  const declared = new Set(recipe?.memberRoles.map((r) => r.key) ?? []);
  return members.filter((m) => declared.has(m.role) && m.disabledAt == null).length;
}

// Route match for an entry that may carry a querystring: active is
// decided by the path.
export function navItemBasePath(item: Pick<NavItem, "path">): string {
  return item.path.split("?")[0]!;
}

// Whether the shell frames its nav around the current project (panel
// postventa v2, README §Navegación): a group-rooted vertical with an
// observation lifecycle and no facility dashboard. Same predicate that
// relabels Tiendas → Proyectos below, plus the lifecycle capability the
// project-scoped Observaciones entry needs — never recipe.slug (§0.4(6)).
export function hasContextualProjectNav(recipe: RecipeMeResponse | undefined): boolean {
  const hasObservationLifecycle = (recipe?.observationStatuses.length ?? 0) > 0;
  const isGroupRooted = recipe?.rootEntity === "group";
  return hasObservationLifecycle && isGroupRooted && !isFacilityDashboardEnabled(recipe);
}

// ADR-0154 — quién ve el análisis de cartera con IA: los roles del tablero
// gerencial en una vertical con proyectos y ciclo de observaciones. El
// servidor además exige que la receta traiga el addendum del memo y
// responde 403 si no; la pantalla lo muestra en vez de esconderlo.
export function canUsePortfolioAnalysis(
  role: string | null | undefined,
  recipe: RecipeMeResponse | undefined
): boolean {
  return isManagementAnalyticsRole(role ?? "") && hasContextualProjectNav(recipe);
}

// Heading copy per group. Pure vocabulary: which group an entry lands in
// is decided by resolveNavItems, so the label needs no recipe.
export function navGroupLabel(group: NavGroup): string {
  switch (group) {
    case "principal":
      return "Operación";
    case "cartera":
      return "Cartera";
    case "gestion":
      return "Gestión";
  }
}

// The entries a contextual-nav vertical lists under «Cartera», in the
// order the handoff shows them (Contratistas · Enlaces compartidos · AI
// Insights). Everything else keeps its static group.
const CARTERA_KEYS = ["contratistas", "enlaces", "ia"];

export function resolveNavItems(input: ResolveNavItemsInput): VisibleNavItem[] {
  const { recipe, session, facilityCounts, adminCounts } = input;
  // A loaded recipe with no dashboard surface (resolveHomeSurface ===
  // "none" — an asset-rooted vertical without `dashboards`, e.g.
  // residential_rental) has no screen to list: every entry would be a
  // dead link into facility vocabulary. The shell renders
  // NoDashboardSurface instead. A still-loading recipe (undefined) is not
  // "none" and keeps the pre-recipe fallback below.
  if (lacksDashboardSurface(recipe)) return [];
  const facilityEnabled = isFacilityDashboardEnabled(recipe);
  const hasObservationLifecycle = (recipe?.observationStatuses.length ?? 0) > 0;
  const isGroupRooted = recipe?.rootEntity === "group";
  const role = session?.organization.role;
  const platformAdmin = session?.user.platformAdmin === true;
  const canManageChecklists = canManageChecklistTemplates(recipe, role, { platformAdmin });
  const canUseIaChat = isIaChatRole(role ?? "");
  const canReadTeamRoster = isTeamRosterReadRole(role ?? "");
  const canImport = canImportUnits(recipe, role, session?.user.platformAdmin);
  const readOnly = isReadOnlyRole(role ?? "");
  // El alcance del rol sale de la receta, no de su clave. Un rol que la
  // receta no declara cae en organizacional, que es el default histórico
  // y el único seguro: esconder Gestión por no encontrar la declaración
  // sería peor que mostrarla.
  const callerScopeType =
    (recipe?.memberRoles ?? []).find((r) => r.key === role)?.scopeType ?? "organization";
  const isOrgScoped = callerScopeType === "organization";

  const canAdminister = canAdministerOrg(recipe, role, { platformAdmin });
  const adminModule = hasAdminModule(recipe);
  const adminSections = declaredAdminSections(recipe);
  // Entradas sueltas que pasaron a ser secciones de Administración. Sin
  // `adminModule` el conjunto es vacío y la lista sale como siempre.
  const absorbed = new Set(adminSections.map((s) => s.replaces).filter(Boolean));

  // "Overview" instantly redirects to /proyectos for a group-rooted,
  // non-facility vertical (OverviewRoute) — a nav row that redirects to
  // the item right below it is a dead row, so drop it rather than ship it.
  // Configuración is hidden from the nav for every vertical and role
  // (founder decision 2026-10-22); its route and screen are kept on
  // purpose, reachable by URL.
  const navItems = NAV_ITEMS.filter(
    (n) =>
      !(n.key === "overview" && isGroupRooted && !facilityEnabled) &&
      n.key !== "configuracion" &&
      n.available !== false &&
      (!n.requiresAdminModule || adminModule) &&
      !absorbed.has(n.key)
  ).flatMap((n) =>
    // Las hijas van ANTES que su padre en la lista plana: quien busca por
    // prefijo de ruta (RouteAnnouncer.titleFor) encuentra primero la
    // sección y no «Administración». El Sidebar las reagrupa debajo del
    // padre igual.
    n.key === ADMIN_NAV_KEY ? [...adminChildren(recipe, adminSections), n] : [n]
  );
  const relabeled = navItems.map((n) => {
    if (n.key === "tiendas" && isGroupRooted && !facilityEnabled) {
      return { ...n, label: recipe.labels.assetGroupPlural, path: "/proyectos" };
    }
    // ADR-0134: facility also assigns by link, so it reaches the roster
    // too — under the shared noun («Contratistas»), see lib/postventa/words.
    if (n.key === "contratistas" && recipe) {
      return { ...n, label: threadWords(recipe).assigneePlural };
    }
    return n;
  });

  const gatedFlat = relabeled.filter(
    (n) =>
      (!n.requiresObservationLifecycle || hasObservationLifecycle) &&
      (!n.requiresChecklistTemplateAdmin || canManageChecklists) &&
      (!n.requiresIaChat || canUseIaChat) &&
      (!n.requiresTeamRosterRead || canReadTeamRoster) &&
      (!n.requiresOrgScope || isOrgScoped || session?.user.platformAdmin === true) &&
      (!n.requiresFacilityDashboard || facilityEnabled) &&
      (!n.requiresUnitImport || canImport) &&
      (!n.requiresContextLayer ||
        (input.contextLayerEnabled === true && (isOrgScoped || callerScopeType === "brand"))) &&
      (!n.hiddenForReadOnly || !readOnly) &&
      (!n.requiresOrgAdmin || canAdminister) &&
      (!n.allow || n.allow(recipe, role, { platformAdmin }))
  );
  // Un padre de acordeón sin ninguna hija visible no se muestra: sería un
  // link a un redirect hacia ningún lado.
  const gated = gatedFlat.filter(
    (n) => n.key !== ADMIN_NAV_KEY || gatedFlat.some((c) => c.parentKey === ADMIN_NAV_KEY)
  );

  // Postventa v2 sidebar: the global entries regroup under «Cartera»,
  // ordered as the handoff lists them; Proyectos stays in `principal`
  // (it is what the project block replaces inside a project) and
  // Equipo / Configuración keep «Gestión» with their current gates
  // (decisión 8 del 2026-09-29). Facility keeps the static groups.
  const visible = hasContextualProjectNav(recipe)
    ? gated
        .map((n) => (CARTERA_KEYS.includes(n.key) ? { ...n, group: "cartera" as const } : n))
        .map((n, i) => ({ n, i }))
        .sort((a, b) => carteraRank(a.n) - carteraRank(b.n) || a.i - b.i)
        .map(({ n }) => n)
    : gated;

  // Counts stay null (no badge, not a misleading 0) until the facility
  // aggregate is actually gated in — non-facility verticals get their
  // own counts source once the cartera surface lands.
  const counts: Record<string, number | null> = {
    tiendas: facilityEnabled ? facilityCounts.assets : null,
    observaciones: facilityEnabled ? facilityCounts.openFindings : null,
    // Only a visible child carries the key, so the gate is already applied.
    [`${ADMIN_NAV_KEY}.equipo`]: adminCounts.equipoActivos,
  };

  return visible.map((n) => ({ ...n, count: counts[n.key] ?? null }));
}

// The Administración children for the declared, available sections. A
// section that replaces a loose entry is that entry (same gates, same
// icon) moved under the parent with the section's own path and title; a
// new section is gated on canAdministerOrg.
function adminChildren(
  recipe: RecipeMeResponse | undefined,
  sections: readonly AdminSectionNav[]
): NavItem[] {
  const words = adminWords(recipe);
  return sections.map((s) => {
    const base: NavItem = (s.replaces && NAV_ITEMS.find((n) => n.key === s.replaces)) || {
      key: s.section,
      label: "",
      path: s.path,
      icon: "Settings",
      group: "gestion",
      requiresOrgAdmin: true,
    };
    return {
      ...base,
      ...(s.allow ? { allow: s.allow } : {}),
      key: `${ADMIN_NAV_KEY}.${s.section}`,
      label: words.sections[s.section],
      path: s.path,
      group: "gestion",
      parentKey: ADMIN_NAV_KEY,
    };
  });
}

function carteraRank(n: NavItem): number {
  if (n.group === "principal") return 0;
  if (n.group === "cartera") return 10 + CARTERA_KEYS.indexOf(n.key);
  return 20;
}

// The one hook the shell's three nav consumers call. The facility query
// is gated on the recipe capability it serves — TanStack Query's
// `enabled` is per-observer (see Shell.tsx), so each consumer repeating
// the same gate is what keeps a non-facility org from firing a doomed
// /facility-dashboard request.
export function useVisibleNavItems(): VisibleNavItem[] {
  const session = useSession();
  const { data: recipe } = useRecipe();
  const facilityEnabled = isFacilityDashboardEnabled(recipe);
  const { data: facility } = useFacilityDashboard(facilityEnabled);
  const { data: org } = useMyOrganization();
  const contextLayerEnabled = isContextLayerEnabled(org);
  const assets = facilityEnabled ? (facility?.totals.assets ?? null) : null;
  const openFindings = facilityEnabled
    ? (facility?.byAsset.reduce((sum, a) => sum + a.openFindingsCount, 0) ?? null)
    : null;
  // The roster behind «Equipo y accesos» (autogestión §2.3), under the same
  // gate as the child itself: the recipe declares the section and the role
  // reads the roster (RequireTeamRosterRead). The screen reads this very
  // cache entry and its mutations invalidate it, so the badge follows a
  // deactivation without a request of its own.
  const rosterEnabled =
    declaredAdminSections(recipe).some((s) => s.section === "equipo") &&
    isTeamRosterReadRole(session?.organization.role ?? "");
  const { data: roster } = useRoster(rosterEnabled);
  const equipoActivos =
    rosterEnabled && roster ? activeTeamMemberCount(roster.members, recipe) : null;

  return useMemo(
    () =>
      resolveNavItems({
        recipe,
        session,
        facilityCounts: { assets, openFindings },
        contextLayerEnabled,
        adminCounts: { equipoActivos },
      }),
    [recipe, session, assets, openFindings, contextLayerEnabled, equipoActivos]
  );
}
